import { JMCLink } from "@components/JMCLink/JMCLink";
import { Icon, jnjLock } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import Html from "@jmc/solid-design-system/src/components/molecules/Html/Html";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiLock } from "@mdi/js";
import type { ValidAuthResults } from "@redux/modules/authValid";
import {
    type ArticleLink,
    type AskTheExpertsLink,
    type AudioLink,
    CMS_PROMOTIONAL_CONTENT,
    type CourseLink,
    type EventCenterLink,
    type EventLink,
    type ExternalResourceLink,
    type IodLink,
    type LmsLink,
    type MaterialLink,
    type MediaCenterLink,
    MenuItemVisibility,
    type MyJmcLink,
    type NewsCenterLink,
    type PageLink,
    type PodcastCenterLink,
    type PodcastLink,
    type ProductGlossaryLink,
    type PubmedLink,
    type RequestMaterialsLink,
    type ScientificPublicationDetailsLink,
    type ScientificPublicationLink,
    type ServiceOverviewLink,
    type SpecialtiesLink,
    type TcpHomeLink,
    type TcpLink,
    type TransferOfValueLink,
    type VideoLink,
} from "@types";
import { isAuthorized } from "@utils/authorizer";
import { getInteralPageLinkParams, getInternalPageLinkAcessLevel, getInternalPageLinkUrl } from "@utils/menu-helper";
import classnames from "classnames";
import type React from "react";
import { useEffect, useState } from "react";

import { SCIENTIFIC_PUBLICATIONS_URL } from "../../../../../jmc_core/src/Constants.mjs";
import style from "./style.module.scss";
import { isCyrillictext } from "./utils";

interface PropTypes {
    id: string;
    title: string;
    reference:
        | PageLink
        | ArticleLink
        | AudioLink
        | CourseLink
        | EventCenterLink
        | EventLink
        | ExternalResourceLink
        | IodLink
        | LmsLink
        | MaterialLink
        | MediaCenterLink
        | MyJmcLink
        | NewsCenterLink
        | PodcastLink
        | PodcastCenterLink
        | ProductGlossaryLink
        | PubmedLink
        | RequestMaterialsLink
        | ScientificPublicationDetailsLink
        | ScientificPublicationLink
        | ServiceOverviewLink
        | SpecialtiesLink
        | TransferOfValueLink
        | VideoLink
        | TcpLink
        | TcpHomeLink
        | AskTheExpertsLink;
    visibility: MenuItemVisibility;
    anchorId?: string;
    params?: { key: string; value: string }[];
    authData: ValidAuthResults;
    isFirstLevel?: boolean;
    linkname?: string;
    labelStyle?: boolean;
}

const hasAccess = (
    reference:
        | PageLink
        | ArticleLink
        | AudioLink
        | CourseLink
        | EventCenterLink
        | EventLink
        | ExternalResourceLink
        | IodLink
        | LmsLink
        | MaterialLink
        | MediaCenterLink
        | MyJmcLink
        | NewsCenterLink
        | PodcastLink
        | PodcastCenterLink
        | ProductGlossaryLink
        | PubmedLink
        | RequestMaterialsLink
        | ScientificPublicationDetailsLink
        | ScientificPublicationLink
        | ServiceOverviewLink
        | SpecialtiesLink
        | TransferOfValueLink
        | VideoLink
        | TcpLink
        | TcpHomeLink
        | AskTheExpertsLink,
    authData: ValidAuthResults
): boolean => {
    const url = getInternalPageLinkUrl(reference);
    const access_level = getInternalPageLinkAcessLevel(reference, url);
    return isAuthorized(authData, access_level, url, (reference as EventLink)?.event_id);
};

const shouldDisplay = (hasAccess: boolean, visibility: MenuItemVisibility): boolean => {
    return hasAccess || visibility === MenuItemVisibility.DISPLAY_ITEM_WITH_LOCK;
};

const InternalMenuLink: React.FunctionComponent<PropTypes> = ({
    id,
    title,
    reference,
    visibility,
    anchorId,
    params,
    authData,
    isFirstLevel = false,
    linkname = null,
    labelStyle = false,
}: PropTypes) => {
    const url = getInternalPageLinkUrl(reference);
    const access_level = getInternalPageLinkAcessLevel(reference, url);
    // if it's a scientific publication it's medical (sp_detail_id)
    // if it has a regulatory status, use that
    // if it doesn't have a regulatory status, assume it's commercial
    const determineCommercial = (): boolean => {
        if ((reference as ScientificPublicationDetailsLink)?.sp_detail_id) {
            return false;
        }
        if (
            reference?.regulatory_status?.promotional_or_medical !== CMS_PROMOTIONAL_CONTENT &&
            reference?.regulatory_status?.promotional_or_medical !== undefined
        ) {
            return false;
        }
        return true;
    };
    const [commercial, setCommercial] = useState(determineCommercial());
    const [displayLink, setDisplayLink] = useState(shouldDisplay(hasAccess(reference, authData), visibility));
    const { jnjFullBranded } = useJnjBranding();

    useEffect(() => {
        // if event, check if medical via axios call
        const determineCommercialEvent = async (): Promise<void> => {
            if ((reference as EventLink)?.event_id) {
                const response = await fetch(
                    `${process.env.GATSBY_API_ORIGIN_URL}/${process.env.GATSBY_API_VERSION}/events/medical/${
                        (reference as EventLink)?.event_id
                    }`
                );
                const data = await response.json();
                if (data?.medical) {
                    setCommercial(false);
                }
            }
        };
        // eslint-disable-next-line no-console
        determineCommercialEvent().catch((err) => console.log(err));
    }, [reference]);

    useEffect(() => {
        setDisplayLink(shouldDisplay(hasAccess(reference, authData), visibility));
    }, [authData, reference]);

    if (!displayLink) {
        return null;
    }

    return (
        <div
            id={id}
            data-linkname={linkname}
            data-test-id={`InternalLink.${title}`}
            className={classnames(
                style.link,
                isFirstLevel ? style.isFirstLevel : null,
                !hasAccess(reference, authData) ? style.showLock : null
            )}
            tabIndex={-1}
        >
            <JMCLink
                params={params && getInteralPageLinkParams(params)}
                disabled={!anchorId && !access_level && !url} //disabled if link is not published or not attached with any page.
                url={url}
                external={false}
                commercial={commercial}
                scientific={url?.includes(SCIENTIFIC_PUBLICATIONS_URL) || false}
                isEvent={!!(reference as EventLink)?.event_id}
                anchor_id={anchorId || ""}
                fullWidth={!(labelStyle && jnjFullBranded)}
                style={jnjFullBranded ? { color: "inherit" } : null}
            >
                {labelStyle && jnjFullBranded ? (
                    <Typography variant="label-03-link" link>
                        <Html data-test-id="Link.Title" isCyrillic={isCyrillictext(title)}>
                            {title}
                        </Html>
                    </Typography>
                ) : (
                    <Html data-test-id="Link.Title" isCyrillic={isCyrillictext(title)}>
                        {title}
                    </Html>
                )}
                <Icon
                    icon={jnjFullBranded ? jnjLock : mdiLock}
                    type={jnjFullBranded ? "jnj" : "mdi"}
                    size={jnjFullBranded ? "small" : "xs"}
                    className={classnames(isFirstLevel ? style.firstLevel : null, style.lock)}
                    color={jnjFullBranded ? "text-light" : "text-dark"}
                    data-test-id="Link.Lock"
                    verticalAlign="middle"
                />
            </JMCLink>
        </div>
    );
};

export { hasAccess, InternalMenuLink, shouldDisplay };
